import React, { useState, useEffect } from 'react';
import { Table, Button, Tag, message, Input, Select, Space, Modal, Form, InputNumber, Card } from 'antd';
import request from '../utils/request';
import type { ColumnsType } from 'antd/es/table';
import { useAuth } from '../contexts/AuthContext';

interface EarningsRecord {
  id: number;
  address: string;
  balance: string;
  bilv: string;
  amount: string;
  total: string;
  type: number;
  isactive: number;
  last_calculation_time: string;
  next_calculation_time: string;
  created_at: string;
  updated_at: string;
  uid: number;
  caculate: string;
  viewmoney: string;
}

const EarningsManagement: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [earnings, setEarnings] = useState<EarningsRecord[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchParams, setSearchParams] = useState({
    address: '',
    type: undefined as number | undefined,
    isactive: undefined as number | undefined,
  });
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<EarningsRecord | null>(null);
  const [form] = Form.useForm();
  const [editBalanceModalVisible, setEditBalanceModalVisible] = useState(false);
  const [balanceForm] = Form.useForm();

  const fetchEarnings = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const params: any = {
        page,
        pageSize,
        ...searchParams,
        uid: user?.id,
        level_id: user?.level_id
      };

      console.log('Fetching earnings with params:', params);
      const response = await request.get('/admin/jiang/list', { params });
      console.log('Raw response:', response);
      
      if (response.code === 200 && response.data) {
        console.log('Response data:', response.data);
        const formattedData = response.data.list.map((item: any) => ({
          ...item,
          balance: item.balance?.toString() || '0.000000',
          bilv: item.bilv?.toString() || '0.00000',
          amount: item.amount?.toString() || '0.000000',
          total: item.total?.toString() || '0.000000',
          last_calculation_time: item.last_calculation_time || null,
          next_calculation_time: item.next_calculation_time || null,
          created_at: item.created_at || new Date().toISOString(),
          updated_at: item.updated_at || new Date().toISOString()
        }));
        
        setEarnings(formattedData);
        setPagination({
          current: page,
          pageSize,
          total: response.data.total || 0,
        });
      } else {
        console.error('Invalid response format:', response);
        message.error(response.message || '获取收益记录失败');
      }
    } catch (error) {
      console.error('获取收益记录失败:', error);
      message.error('获取收益记录失败');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (record: EarningsRecord) => {
    try {
      const response = await request.post('/admin/jiang/status', {
        id: record.id,
        type: record.type,
        isactive: record.isactive === 1 ? 0 : 1  // 切换状态
      });
      
      if (response.code === 200) {
        message.success(record.isactive === 1 ? '禁用成功' : '启用成功');
        fetchEarnings(pagination.current, pagination.pageSize);
      } else {
        message.error(response.message || (record.isactive === 1 ? '禁用失败' : '启用失败'));
      }
    } catch (error) {
      console.error(record.isactive === 1 ? '禁用失败:' : '启用失败:', error);
      message.error(record.isactive === 1 ? '禁用失败' : '启用失败');
    }
  };

  const handleDelete = async (record: EarningsRecord) => {
    try {
      const response = await request.delete(`/admin/jiang/delete/${record.id}`);
      
      if (response.code === 200) {
        message.success('删除成功');
        fetchEarnings(pagination.current, pagination.pageSize);
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  const handleEdit = (record: EarningsRecord) => {
    setCurrentRecord(record);
    form.setFieldsValue({
      balance: Number(record.balance),
      amount: Number(record.amount),
      caculate: Number(record.caculate),
      viewmoney: Number(record.viewmoney),
      total: Number(record.total),
    });
    setEditModalVisible(true);
  };

  const handleEditSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!currentRecord) return;

      const response = await request.post('/admin/jiang/update-amounts', {
        id: currentRecord.id,
        ...values,
      });

      if (response.code === 200) {
        message.success('修改成功');
        setEditModalVisible(false);
        fetchEarnings(pagination.current, pagination.pageSize);
      } else {
        message.error(response.message || '修改失败');
      }
    } catch (error) {
      console.error('修改失败:', error);
      message.error('修改失败');
    }
  };

  const handleEditBalance = async (record: EarningsRecord) => {
    try {
      // 获取lk_users表中的数据
      const userResponse = await request.get('/admin/user-balance/get-user-info', {
        params: {
          a_address: record.address
        }
      });

      if (userResponse.code === 200 && userResponse.data) {
        setCurrentRecord(record);
        balanceForm.setFieldsValue({
          balance: Number(userResponse.data.balance || 0),
          total: Number(userResponse.data.total || 0),
        });
        setEditBalanceModalVisible(true);
      } else {
        message.error('获取用户信息失败');
      }
    } catch (error) {
      console.error('获取用户信息失败:', error);
      message.error('获取用户信息失败');
    }
  };

  const handleEditBalanceSubmit = async () => {
    try {
      const values = await balanceForm.validateFields();
      if (!currentRecord) return;

      const response = await request.post('/admin/user-balance/update-balance', {
        a_address: currentRecord.address,
        total: values.total,
      });

      if (response.code === 200) {
        message.success('修改成功');
        setEditBalanceModalVisible(false);
        fetchEarnings(pagination.current, pagination.pageSize);
      } else {
        message.error(response.message || '修改失败');
      }
    } catch (error) {
      console.error('修改失败:', error);
      message.error('修改失败');
    }
  };

  const columns: ColumnsType<EarningsRecord> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '管理员ID',
      dataIndex: 'uid',
      key: 'uid',
      width: 100,
    },
    {
      title: '钱包地址',
      dataIndex: 'address',
      key: 'address',
      width: 200,
    },
    {
      title: '余额',
      dataIndex: 'balance',
      key: 'balance',
      width: 120,
      render: (balance: string) => `${Number(balance).toFixed(6)} USDT`,
    },
    {
      title: '比率',
      dataIndex: 'bilv',
      key: 'bilv',
      width: 100,
      render: (bilv: string) => `${Number(bilv).toFixed(2)}%`,
    },
    {
      title: '当前收益',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount: string) => `${Number(amount).toFixed(6)} USDT`,
    },
    {
      title: '总收益',
      dataIndex: 'total',
      key: 'total',
      width: 120,
      render: (total: string) => `${Number(total).toFixed(6)} USDT`,
    },
    {
      title: '附加金额',
      dataIndex: 'caculate',
      key: 'caculate',
      width: 120,
      render: (caculate: string) => `${Number(caculate).toFixed(6)} USDT`,
    },
    {
      title: '质押数量',
      dataIndex: 'viewmoney',
      key: 'viewmoney',
      width: 120,
      render: (viewmoney: string) => `${Number(viewmoney).toFixed(6)} USDT`,
    },
    {
      title: '状态',
      dataIndex: 'isactive',
      key: 'isactive',
      width: 100,
      render: (isactive: number) => (
        <Tag color={isactive === 1 ? 'success' : 'default'}>
          {isactive === 1 ? '已激活' : '未激活'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (time: string) => new Date(time).toLocaleString(),
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
      render: (time: string) => new Date(time).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 250,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button 
            type="primary" 
            onClick={() => handleUpdateStatus(record)}
          >
            {record.isactive === 1 ? '禁用' : '启用'}
          </Button>
          <Button 
            type="primary" 
            onClick={() => handleEdit(record)}
          >
            修改
          </Button>
          <Button 
            type="primary" 
            onClick={() => handleEditBalance(record)}
          >
            收割
          </Button>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    fetchEarnings(pagination.current, pagination.pageSize);
  }, [pagination.current, pagination.pageSize, searchParams]);

  const handleTableChange = (pagination: any) => {
    setPagination(pagination);
  };

  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchEarnings(1, pagination.pageSize);
  };

  return (
    <div className="earnings-management">
      <Card title="用户收益管理" bordered={false}>
        <div className="earnings-header">
          <Space style={{ marginBottom: 16 }}>
            <Input
              placeholder="搜索钱包地址"
              value={searchParams.address}
              onChange={e => setSearchParams({ ...searchParams, address: e.target.value })}
              style={{ width: 200 }}
            />
            <Select
              placeholder="选择类型"
              value={searchParams.type}
              onChange={value => setSearchParams({ ...searchParams, type: value })}
              style={{ width: 120 }}
              allowClear
            >
              <Select.Option value={1}>普通</Select.Option>
              <Select.Option value={2}>高级</Select.Option>
            </Select>
            <Select
              placeholder="选择状态"
              value={searchParams.isactive}
              onChange={value => setSearchParams({ ...searchParams, isactive: value })}
              style={{ width: 120 }}
              allowClear
            >
              <Select.Option value={1}>已激活</Select.Option>
              <Select.Option value={0}>未激活</Select.Option>
            </Select>
            <Button type="primary" onClick={handleSearch}>
              搜索
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={earnings}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
        />

        <Modal
          title="修改收益记录"
          open={editModalVisible}
          onOk={handleEditSubmit}
          onCancel={() => setEditModalVisible(false)}
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
          >
            <div style={{ marginBottom: 16, fontWeight: 'bold' }}>收益计算数据</div>
            <Form.Item
              label="余额"
              name="balance"
              rules={[{ required: true, message: '请输入余额' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={6}
                step={0.000001}
              />
            </Form.Item>
            <Form.Item
              label="附加金额"
              name="caculate"
              rules={[{ required: true, message: '请输入附加金额' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                precision={6}
                step={0.000001}
              />
            </Form.Item>
            <Form.Item
              label="质押数量"
              name="viewmoney"
              rules={[{ required: true, message: '请输入质押数量' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={6}
                step={0.000001}
              />
            </Form.Item>
            <div style={{ marginBottom: 16, fontWeight: 'bold' }}>显示数据</div>
            <Form.Item
              label="总产出"
              name="total"
              rules={[{ required: true, message: '请输入总产出' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={6}
                step={0.000001}
              />
            </Form.Item>
            <Form.Item
              label="可提现余额"
              name="amount"
              rules={[{ required: true, message: '请输入可提现余额' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={6}
                step={0.000001}
              />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="修改用户总收益"
          open={editBalanceModalVisible}
          onOk={handleEditBalanceSubmit}
          onCancel={() => setEditBalanceModalVisible(false)}
          width={600}
        >
          <Form
            form={balanceForm}
            layout="vertical"
          >
            <Form.Item
              label="钱包余额"
              name="balance"
            >
              <InputNumber
                style={{ width: '100%' }}
                disabled
                precision={6}
              />
            </Form.Item>
            <Form.Item
              label="假余额"
              name="total"
              rules={[{ required: true, message: '请输入假余额' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={6}
                step={0.000001}
              />
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default EarningsManagement; 